// The remote pictures the hoops page draws, and the ONE place that says what they are. P6-328b3.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 EVERY PICTURE HERE IS A REQUEST FROM A MEMBER'S OWN BROWSER TO nba.com
// ═══════════════════════════════════════════════════════════════════════════
//
// Jordan, 2026-10-04: *"headshots and logos would be ideal"*, accepting that members' browsers fetch
// them from the NBA's CDN. Nothing is proxied or stored: this page names a URL and the browser asks for
// it, so the two things worth being exact about are WHICH hosts it may name and WHAT it tells them.
//
//   which   a logo URL comes out of a blob (`index.json` `teams[].logo`), so it is accepted only if it
//           is exactly the CDN's logo URL for THAT team (`logoUrl`). A headshot URL is built here from a
//           player's `person_id` and from nothing else (`headshotUrl`). Anything else is `null`, and a
//           `null` is drawn as the design's disc, not as a request.
//   what    `Marks.jsx` gives every `<img>` `referrerPolicy="no-referrer"`, so the portal's address (and
//           so which member is looking at which page) is never sent to nba.com.
//
// ⚠️ MEASURED BY THE MANAGER THE DAY IT WAS ASKED FOR (200 on all four, with or without a Referer):
//
//   logo        …/logos/nba/{teamId}/global/L/logo.svg                 image/svg+xml  ~13 KB
//   headshot    …/headshots/nba/latest/260x190/{personId}.png          image/webp     ~8 KB
//   headshot    …/headshots/nba/latest/1040x760/{personId}.png         image/webp     ~112 KB
//   unknown id  (either size)                                          a ~1.4 KB silhouette, NOT an error
//
// So an `error` event means the CDN was refused, unreachable or answered with something that is not a
// picture, and never "that player has no photo": a wrong id is a silhouette, and a silhouette is what
// gets drawn.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE SMALL HEADSHOT, BUILT HERE; THE LARGE ONE IS THE BOT'S AND IS NEVER READ
// ═══════════════════════════════════════════════════════════════════════════
//
// The bot publishes `index.json` `players[].headshot`, and it is the 1040x760 URL: about 112 KB for a
// face that is drawn 38 pixels wide, fourteen times the 8 KB of the 260x190 one. This page therefore
// builds the small URL from the `person_id` the game's own box score carries (one helper, below) and
// does not read the published one at all, which is also why the Function sends the board `teams` and
// not `players`. `tests/test_hoops_site_fixtures.py` reads the four constants below and asserts them
// against what the bot publishes for every team and every player (CLAUDE.md gotcha 21: two
// implementations of one URL, asserted against each other), so the bot moving a path fails a suite
// instead of turning every face into its initials.

/** The CDN's logo for a team: `{LOGO_PREFIX}{teamId}{LOGO_SUFFIX}`. What the bot publishes, character for character. */
export const LOGO_PREFIX = 'https://cdn.nba.com/logos/nba/'
export const LOGO_SUFFIX = '/global/L/logo.svg'

/** The CDN's SMALL headshot: `{HEADSHOT_PREFIX}{personId}{HEADSHOT_SUFFIX}`. The bot's is the same with `1040x760`. */
export const HEADSHOT_PREFIX = 'https://cdn.nba.com/headshots/nba/latest/260x190/'
export const HEADSHOT_SUFFIX = '.png'

/**
 * A team's logo URL, or `null`.
 *
 * `team` is an entry of the board's `teams` (the bot's own: `id`, `tricode`, `logo`, …). The published
 * `logo` is used only when it is the CDN's logo URL for that same team: the right host over https, the
 * right path, and the id in it is `team.id`. A different host, a different path, a different team's id
 * or no `logo` at all is `null`, never "draw it anyway": the id check is what stops one team's logo
 * from being drawn on another's card if the table is ever wrong.
 */
export function logoUrl(team) {
  const url = team?.logo
  if (typeof url !== 'string' || !Number.isInteger(team?.id)) return null
  if (!url.startsWith(LOGO_PREFIX) || !url.endsWith(LOGO_SUFFIX)) return null
  const id = url.slice(LOGO_PREFIX.length, url.length - LOGO_SUFFIX.length)
  return /^[0-9]+$/.test(id) && id === String(team.id) ? url : null
}

/**
 * A player's small headshot URL, or `null`.
 *
 * The 260x190 size, for anything small (every face this page draws is). Built from the `person_id` of
 * a box-score player and from nothing else: not from a published URL, and not from a name.
 */
export function headshotUrl(personId) {
  return Number.isInteger(personId) && personId > 0 ? `${HEADSHOT_PREFIX}${personId}${HEADSHOT_SUFFIX}` : null
}
